import { Box, Button, Flex, Link, Text, Tooltip } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useReleaseTags } from "../../hooks/useReleaseTags";
import { CTAButtons } from "./CTAButtons";
import { useHookstate } from "@hookstate/core";
import { useMobileBreakpoint } from "../../../../utils/useBreakpoints";
import { useSlideInMenu } from "../../../../components/slideInMenu/hooks/useSlideInMenu";
import { readerDataStore } from "../../../../hookstate/readerDataStore";
import { readerNavigationContext } from "../../../../hookstate/readerNavigationContextStore";
import { CollectibleBadge } from "../../../../components/cards/components/CollectibleBadge";
import { CaretLeftLargeIcon } from "../../../../components/icons/CaretLeftLargeIcon";
import { useNavigate } from "react-router";
import { useWasSameOrigin } from "../../../../hooks/useWasSameOrigin";

export function ReleaseSummary() {
  const releaseData = useHookstate(readerDataStore.releaseData).get({
    noproxy: true,
  });
  const tags = useReleaseTags(releaseData);
  const isMobile = useMobileBreakpoint();
  const { openArtistProfile } = useSlideInMenu();
  const readerContext = useHookstate(readerNavigationContext).get({
    noproxy: true,
  });
  const navigate = useNavigate();
  const wasSameOrigin = useWasSameOrigin();

  const showCollectibleBadge =
    Boolean(releaseData?.premium_only) && !releaseData?.is_free;

  return (
    <Box
      as={motion.div}
      animate={{ x: [-10, 25], opacity: [0, 1] }}
      position="absolute"
      top="85px"
      height="calc(100% - 200px)"
      width="calc(100% - 88px)"
      left={0}
      display="flex"
      flexDirection="column"
      gap="24px"
    >
      <Flex flexDirection="column">
        <Tooltip
          variant="navbar"
          label="Go back"
          aria-label="Go back"
          placement="right"
        >
          <Button
            variant="iconOnly"
            minWidth="0"
            marginBottom="32px"
            onClick={() => (wasSameOrigin ? navigate(-1) : navigate("/"))}
            gap="12px"
          >
            <CaretLeftLargeIcon boxSize="32px" />
          </Button>
        </Tooltip>
        {showCollectibleBadge && <CollectibleBadge />}
        <Link
          aria-label={`Read ${releaseData?.comic?.name}`}
          href={releaseData?.comic?.url}
          width="fit-content"
          mt={showCollectibleBadge ? "16px" : undefined}
        >
          <Text
            color="reader.neutral.200"
            fontSize={isMobile ? "16px" : "20px"}
            fontWeight="600"
            lineHeight={isMobile ? "24px" : "28px"}
            _hover={{
              textDecoration: "underline",
            }}
            width="fit-content"
          >
            {releaseData?.comic?.name}
          </Text>
        </Link>
        <Text
          fontSize={isMobile ? "24px" : "30px"}
          fontWeight="600"
          lineHeight={isMobile ? "32px" : "36px"}
          textOverflow="clip"
        >
          {releaseData?.title}
        </Text>
        <Box
          aria-label={`View ${releaseData?.comic?.artist?.roman_name}`}
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            openArtistProfile(releaseData?.comic?.artist?.name ?? "", {
              ...(readerContext ?? {}),
              area: "consumption",
            });
          }}
          width="fit-content"
        >
          <Text
            color="reader.blue.200"
            fontSize={isMobile ? "16px" : "20px"}
            fontWeight="600"
            lineHeight={isMobile ? "24px" : "28px"}
            _hover={{
              textDecoration: "underline",
            }}
            width="fit-content"
          >
            {releaseData?.comic?.artist?.roman_name}
          </Text>
        </Box>
      </Flex>
      <Flex flexDirection="column" gap="6px">
        {tags.map((tag, i) => {
          if (!tag) return null;
          const { text, borderColor } = tag;
          return (
            <Box
              key={i}
              py="4px"
              px="12px"
              borderRadius="full"
              border="1px solid"
              borderColor={borderColor ? borderColor : "transparent.white.40"}
              width="fit-content"
            >
              <Text
                fontSize="10px"
                fontWeight="600"
                lineHeight="15px"
                textTransform="uppercase"
              >
                {text}
              </Text>
            </Box>
          );
        })}
      </Flex>
      {releaseData && <CTAButtons releaseData={releaseData} />}
    </Box>
  );
}
